import React, {forwardRef, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Intent, Callout} from '@blueprintjs/core';

import {IState} from '../../../store/reducers';
import {ApiStatus} from '../../../models';
import {stopSqlBlockExecution} from '../../../store/actions/blockResultActions';
import {ContentCard, ContentToolbar, ExportButton} from '../components';
import {toggleAceReadOnly} from '../utils';
import SqlEditor from './Editor';
import {connectionToSelectOption, createDefaultContent} from './utils';
import NoDatabase, {NoDatabaseCallout} from './NoDatabase';
import {
  useCreateBlock,
  useExecuteRequest,
  useUpdateBlockContentJson,
  useGetResult,
} from '../hooks';
import DownloadResultButton from './DownloadResultButton';
import SelectLimit from './SelectLimit';
import Result from './Result';
import QueryDetails from './QueryDetails';
import {COMMANDS, onExecuteKeypress} from './commands';
import {BaseBlock, ContentOptions, ContentActions} from '../types';
import {defaultContentOptions, defaultContentActions} from '../constants';

import './Sql.scss';

interface SqlProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const Sql = forwardRef(({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: SqlProps, ref: any) => {
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const [formErrors, setFormErrors] = useState<{ [key: string]: string | null }>({});

  /*
   * TODO: useReducer instead of useRef
   * NOTE: useRef values don't trigger re-renders
   * NOTE: we use useRef in order to have the latest values in the onExecute function (values by reference)
   */
  const databaseIdRef = useRef<string | null>(content.database_id || null);
  const limitRef = useRef<string | null>(content.limit !== undefined ? content.limit : '1000');
  const editorRef = useRef<any>();
  const uid = content.uid;

  const databases = useSelector((state: IState) => state.databases.databases);
  const loadingStatus = useSelector((state: IState) => state.databases.loadingListStatus);
  const loaded = loadingStatus === ApiStatus.LOADED;
  const {executing, executeRequest} = useExecuteRequest(uid);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const noDatabases = loaded && databases.length === 0;
  const dispatch = useDispatch();

  const created = useCreateBlock(content, createDefaultContent());
  const {result, getResult} = useGetResult(uid, content.result_id, created ? false : options.loadResult);

  const onExecute = () => {
    let isValid = true;
    const errors: { [key: string]: string | null } = {};

    if (!editorRef.current) {
      return;
    }

    const aceEditor = editorRef.current.getEditor();
    const selectedCode = aceEditor.getSelectedText();
    const allCode = aceEditor.getSession().getValue();

    const sqlText = selectedCode ? selectedCode : allCode;

    if (!sqlText) {
      errors.sqlText = "SQL is required";
      isValid = false;
    }

    setFormErrors(errors);

    if (!isValid) {
      return;
    }

    if (!databaseIdRef.current) {
      return;
    }

    const block = {
      content_json: {
        database_id: databaseIdRef.current,
        limit: limitRef.current,
        sql: sqlText,
      }
    };
    executeRequest(block);
  };

  const onCancelExecution = (uid: string, key: string) => {
    dispatch(stopSqlBlockExecution(uid, key));
  };

  const onSelectResult = (key: string) => {
    getResult(key);
  }

  const onSqlChange = (value: string) => {
    const aceEditor = editorRef.current.getEditor() || null;

    if (!aceEditor) {
      return;
    } else if (aceEditor.getReadOnly()) {
      return;
    }

    setFormErrors((prevErrors) => {
      return {
        ...prevErrors,
        sqlText: value ? null : prevErrors.sqlText,
      };
    });

    updateBlockContentJson('sql', value);
  };

  const onDatabaseIdChange = (databaseId: string) => {
    databaseIdRef.current = databaseId;
    updateBlockContentJson('database_id', databaseId, false);
  };

  const onLimitChange = (limit: string) => {
    limitRef.current = limit;
    updateBlockContentJson('limit', limit);
  };

  const onExportOptionsChange = (key: string, value: any) => {
    updateBlockContentJson(`export_options.${key}`, value);
  }

  const onEditorLoad = (editor: any) => {
    if (editor.commands.commands.hasOwnProperty(COMMANDS.EXECUTE_KEYPRESS)) {
      return;
    }
    editor.commands.addCommand(onExecuteKeypress(onExecute));
  };

  const onPreview = (data: any) => {
    const aceEditor = editorRef.current?.getEditor() || null;

    if (!aceEditor) {
      return;
    }

    if (!data) {
      // restore
      toggleAceReadOnly(aceEditor, null, false);
    } else {
      toggleAceReadOnly(aceEditor, data.sql, true);
    }
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={uid}
      disabled={noDatabases || options.readOnly}
      noContentElement={noDatabases ? <NoDatabase
        contentId={uid as string}
        onDelete={actions.onDelete}
        showDelete={options.showDelete}
      /> : undefined}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__sql">
        <ContentToolbar
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          onDelete={actions.onDelete}
          selected={databaseIdRef.current || ''}
          onSelect={onDatabaseIdChange}
          options={databases.map(connectionToSelectOption)}
          updating={updating}
          onPreview={onPreview}
          showPreview={options.showPreview}
          showDelete={options.showDelete}
          showSave={options.showSave}
          disabled={options.readOnly}
        />
        <div className="content-card__content__form">
          {
            !databaseIdRef.current ? <NoDatabaseCallout/> : (
              <>
                <div className="editor">
                  <SqlEditor
                    defaultValue={content.sql || ''}
                    onLoad={onEditorLoad}
                    onChange={onSqlChange}
                    contentId={content.id}
                    ref={editorRef}
                    disabled={options.readOnly}
                  />
                </div>
                {formErrors.sqlText && (
                  <Callout intent={Intent.DANGER} className="sqlTextError">
                    {formErrors.sqlText}
                  </Callout>
                )}
                <div className="editor-footer">
                  <SelectLimit
                    defaultLimit={limitRef.current}
                    onChange={onLimitChange}
                    disabled={options.readOnly}
                  />
                  <div>
                    <QueryDetails
                      status={result?.status as string}
                      query={result?.value?.query}
                    />
                    <DownloadResultButton
                      uid={content.uid as string}
                      result={result}
                    />
                  </div>
                </div>
                <Result
                  key={'result_' + content.id}
                  result={result}
                  onSelectResult={onSelectResult}
                  onCancelExecution={onCancelExecution}
                />
              </>
            )
          }
        </div>
      </div>
      <ExportButton
        show={options.showExportOptions as boolean && databaseIdRef.current !== null}
        contentId={content.uid as string}
        csv={true}
        xlsx={true}
        html_table={true}
        onChange={onExportOptionsChange}
        disabled={options.readOnly}
      />
    </ContentCard>
  );
});
